import { useContext } from 'external/react'
import { clsx, styled, TableCell as MuiTableCell, TableHead, TableRow, TableSortLabel, Tooltip } from 'external/material'
import { dayjs } from 'external/dayjs'

import {TeacherContext} from 'teacher/context'

import Options from './HeaderOptions'

const StyledTableHead = styled(TableHead)(tableHeadStyles)

export default function GradesHead(props) {

  const context = useContext(TeacherContext)
  const {columns, createSortHandler, orderBy} = props
  const {translate, query} = context
  const {gradeFilter: queryGradeFilters} = query

  const filterGrades = (column) => {
    const queryFiltersValue = queryGradeFilters ?? []
    const queryFilters = Array.isArray(queryFiltersValue) ? queryFiltersValue : [queryFiltersValue]

    if(queryFilters.includes('all') || queryFilters.length === 0){
      return true
    }

    const dateAssigned = dayjs(new Date(column.date))
    const creationDate = dayjs(new Date(column.start))

    if (queryFilters.includes(column.type)) {
      return true;
    }


    const isManual = dateAssigned.isAfter(creationDate);
    const isAdjust = dateAssigned.isBefore(creationDate); 

    if (isManual && queryFilters.includes('manual')) {
      return true;
    }

    if (isAdjust && queryFilters.includes('adjust')) {
      return true;
    }

    return false

  }
  return (
    <StyledTableHead>
      <TableRow className="headersRow">
        <TableCell
          id="student-name-cell"
          description={translate('student')}
          sortDirection={orderBy.order}
          activeSort={orderBy.column == 'student'} 
          sortHandler={createSortHandler('student')}
          cellClass="student"
          labelClass="studentLabel"
        >
          <span>{translate('student')}</span>
        </TableCell>
        <TableCell
          id="global-summary-cell"
          align="center"
          description={translate('grade-summary-header')}
          sortDirection={orderBy.order}
          activeSort={orderBy.column == 'summary'} 
          sortHandler={createSortHandler('summary')}
          cellClass="summary"
          labelClass="summaryLabel"
        >
          <span>{translate('grade-summary-header')}</span>
        </TableCell>
        {columns.filter(filterGrades).map(column => {
          const creationDate = dayjs(new Date(column.start))
          const dateAssigned = dayjs(new Date(column.date || column.start))
          const description = `${column.title} [${dateAssigned.format('MM/DD/YYYY')}]`
          const isExternal = Boolean(column?.external)
          const classNameDate = dateAssigned.isBefore(creationDate) ? 'gradeLabelPastDate' : 'gradeLabelPresentDate'
          return (
            <TableCell
              key={column.id}
              id={`${column.label}-cell`}
              description={description}
              sortDirection={orderBy.order}
              activeSort={orderBy.column == column.label} 
              sortHandler={createSortHandler(column.label)}
              cellClass="grade"
              labelClass={`gradeLabel ${isExternal ? classNameDate : ''}`}
              column={column}
            >
              <span className="gradeText">
                <span className="gradeName">{column.label}</span>
                <span className="gradeTotal">{column.total}</span>
              </span>
            </TableCell>
          )
        })}
        <MuiTableCell className="filler" style={{top:92}} align="right" />
      </TableRow>
    </StyledTableHead>
  )
}

function TableCell (props) {

  const {
    align, disablePadding, description,
    sortDirection, activeSort, sortHandler,
    column,
    cellClass, labelClass, containerClass
  } = props
  const {properties: {ignore}} = column || {properties: {ignore: false}}

  return (<MuiTableCell
    align = {align || 'left'}
    padding={disablePadding ? 'none' : 'normal'}
    className={clsx("headerCell", cellClass)}
    sortDirection={sortDirection}
    style={{top:92}}
  >
    <div className={clsx("container", {ignore})}>
      {column && <Options {...{column}} />}
      <div className="cellContainer">
        <TableSortLabel
          className={labelClass}
          active={activeSort}
          direction={sortDirection}
          onClick={sortHandler}
        >
          <Tooltip title={description}>
            <div className={clsx("label", containerClass)}>
              {props.children}
              {activeSort && (
                <span className="visuallyHidden" />
              )}
            </div>
          </Tooltip>
        </TableSortLabel>
      </div>
    </div>
  </MuiTableCell>)
}

function tableHeadStyles() {
    return {
        "& .gradeName": {
          fontWeight: 'bold'
        },
        "& .gradeText": {
          display: 'flex',
          flexDirection: 'column',
        },
        "& .center": {
          textAlign: 'center'
        },
        "& .headersRow": {
          color: '#50566e',
          '& > th': {
            borderTop: 'solid 1px #d8dbe0',
            backgroundColor: '#f1faff'
          }
        },
        "& .headerCell": {
          padding: 0,
          color: '#4f576e',
          fontWeight: 'bold',
          borderLeft: 'solid 1px #d8dbe0',
          borderRight: 'solid 1px #d8dbe0',
          '&:first-of-type': {
            borderLeft: 0
          },
          '& .MuiTableSortLabel-active': {
            color: '#4f576e',
          }
        },
        "& .student": {
          padding: '0 12px',
          width: 250,
          minWidth: 200,
          maxWidth: 300,
        },
        "& .summary": {
          minWidth: 100,
          width: 200,
          padding: '0 12px',
          maxWidth: 250,
          '& .cellContainer': {
            paddingLeft: 12,
            justifyContent: 'center'
          }
        },
        "& .grade": {
          minWidth: 70,
          maxWidth: 70,
          padding: 0
        },
        "& .gradeTotal": {
          fontWeight: 500
        },
        "& .gradeLabel": {
          width: '100%',
          justifyContent: 'center',
          paddingLeft: 20
        },
        "& .gradeLabelPastDate": {
          backgroundColor: 'rgba(234, 185, 237, 0.4)'
        },
        "& .gradeLabelPresentDate": {
          backgroundColor: 'rgba(205, 240, 192, 0.4)'
        },
        "& .container": {
          position: 'relative',
          top: 0,
          height: 60,
          '&.ignore': {
            background: 'repeating-linear-gradient(110deg, rgb(160 156 156) 5px, rgb(150 136 136 / 69%) 9%)',
          }
        },
        "& .cellContainer": {
          position: 'absolute',
          zIndex: 10,
          top: 0,
          width: '100%',
          height: '100%',
          display: 'flex',
        },
        "& .visuallyHidden": {
          border: 0,
          clip: 'rect(0 0 0 0)',
          height: 1,
          margin: -1,
          overflow: 'hidden',
          padding: 0,
          position: 'absolute',
          top: 20,
          width: 1,
        },
      }
}
